import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { useIsHomeHealthEnabled } from "@src/appV2/ExperimentalHomeHealth/lib/useIsHomeHealthEnabled";
import { prefetchScheduleData } from "@src/appV2/redesign/DailyView/prefetchScheduleData";
import { preloadScheduleRoute } from "@src/appV2/redesign/DailyView/preloadSchedule";
import { useScheduleTabEnabled } from "@src/appV2/redesign/DailyView/hooks/useScheduleTabEnabled";
import { useIsMonthlyViewEnabled } from "@src/appV2/redesign/MonthlyView/hooks/useIsMonthlyViewEnabled";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { AdminMenuPrivileges } from "../privilegesProviders/AdminMenuPrivilegesProvider";
import { WorkerMenuPrivileges } from "../privilegesProviders/WorkerMenuPrivilegesProvider";
import { WorkplaceMenuPrivileges } from "../privilegesProviders/WorkplaceMenuPrivilegesProvider";
import { NavigationMenu } from "./types";
import { getAdminNavigationMenu } from "./useGetAdminNavigationMenu";
import { getWorkerNavigationMenu } from "./useGetWorkerNavigationMenu";
import { getWorkplaceNavigationMenu } from "./useGetWorkplaceNavigationMenu";

interface NavigationMenuOptions {
  unreadChannelCount?: number;
  pendingExtraWorkedTimeRequestCount?: number;
  isMobile?: boolean;
}

export function useGetNavigationMenu(
  isAdmin: boolean,
  adminMenuPrivileges: AdminMenuPrivileges,
  isWorker: boolean,
  workerMenuPrivileges: WorkerMenuPrivileges,
  isWorkplace: boolean,
  workplaceMenuPrivileges: WorkplaceMenuPrivileges,
  currentPath: string,
  options: NavigationMenuOptions = {}
): NavigationMenu {
  const analyticsConfig = useCbhFlag(CbhFeatureFlag.FACILITY_EXPLO_ANALYTICS_ENABLED, {
    defaultValue: [],
  });
  const isMonthlyViewEnabled = useIsMonthlyViewEnabled();
  const isScheduleTabEnabled = useScheduleTabEnabled();
  const isHomeHealthEnabled = useIsHomeHealthEnabled();
  const queryClient = useQueryClient();
  const { profile } = useSession();
  const preloadSchedule = useCallback(() => {
    preloadScheduleRoute();

    if (!isWorkplace || !workplaceMenuPrivileges.dailyView) {
      return;
    }

    void prefetchScheduleData({
      queryClient,
      facilityId: profile.userId,
      timezone: profile.tmz,
    });
  }, [isWorkplace, workplaceMenuPrivileges.dailyView, queryClient, profile.userId, profile.tmz]);

  if (isAdmin) {
    return getAdminNavigationMenu(adminMenuPrivileges, currentPath);
  } else if (isWorker) {
    return getWorkerNavigationMenu(workerMenuPrivileges, currentPath);
  } else if (isWorkplace) {
    return getWorkplaceNavigationMenu(workplaceMenuPrivileges, currentPath, analyticsConfig, {
      unreadChannelCount: options.unreadChannelCount,
      pendingExtraWorkedTimeRequestCount: options.pendingExtraWorkedTimeRequestCount,
      isMobile: options.isMobile,
      isMonthlyViewEnabled,
      isClassicCalendarPathActive: isHomeHealthEnabled || isScheduleTabEnabled,
      onSchedulePreload: preloadSchedule,
    });
  } else {
    return { items: [] };
  }
}
